import React, { useState } from 'react';
import { MenuCategory } from '../../../Redux/MenuCategory/menuCategorySlice';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  addMenuCategory,
  deleteMenuCategory,
  reorderMenuCategories,
  fetchMenuCategories,
} from '../../../Redux/MenuCategory/menuCategoryApi';
import { AppDispatch, RootState } from '../../../Redux/Store';
import { reorderCategoriesLocally } from '../../../Redux/MenuCategory/menuCategorySlice';
import { useDraggable } from '../../../hooks/useDraggable';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';

interface CategoryManagementProps {
  newCategoryName: string;
  setNewCategoryName: React.Dispatch<React.SetStateAction<string>>;
  onDragEnd?: (result: { source: { index: number }; destination?: { index: number } }) => Promise<void>;
}

interface DraggableCategoryProps {
  category: MenuCategory;
  handleDeleteCategory: (id: string) => void;
}

const DraggableCategory: React.FC<DraggableCategoryProps> = ({
  category,
  handleDeleteCategory,
}) => {
  const { setNodeRef, setActivatorNodeRef, style, attributes, listeners } = useDraggable(
    category.id,
    { direction: 'vertical' }
  );

  return (
    <ListItem
      component="li"
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        mb: 1,
        borderRadius: 1,
        boxShadow: 1,
      }}
    >
      <Box
        ref={setActivatorNodeRef}
        sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon sx={{ mr: 2, color: 'text.secondary' }} />
      </Box>
      <ListItemText primary={category.name} />
      <IconButton
        edge="end"
        aria-label="delete"
        onClick={() => handleDeleteCategory(category.id)}
      >
        <DeleteIcon />
      </IconButton>
    </ListItem>
  );
};

const CategoryManagement: React.FC<CategoryManagementProps> = ({
  newCategoryName,
  setNewCategoryName,
  onDragEnd,
}) => {
  const { store_id } = useParams<{ store_id: string }>();
  const dispatch: AppDispatch = useDispatch();
  const [hasChanges, setHasChanges] = useState(false);
  const categories = useSelector((state: RootState) => state.menuCategory.categories);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 50,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddCategory = async () => {
    if (!newCategoryName || !store_id) {
      return;
    }
    try {
      await dispatch(addMenuCategory({ storeId: store_id, name: newCategoryName })).unwrap();
      await dispatch(fetchMenuCategories(store_id)).unwrap();
      setNewCategoryName('');
    } catch (error) {
      console.error('カテゴリの追加に失敗しました。', error);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!store_id) return;
    try {
      await dispatch(deleteMenuCategory({ storeId: store_id, categoryId: id })).unwrap();
      await dispatch(fetchMenuCategories(store_id)).unwrap();
    } catch (error) {
      console.error('カテゴリの削除に失敗しました。', error);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !store_id) return;

    if (active.id !== over.id) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id);
      const newIndex = categories.findIndex((cat) => cat.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        dispatch(reorderCategoriesLocally({ fromIndex: oldIndex, toIndex: newIndex }));
        setHasChanges(true);
      }
    }
  };

  const handleSaveChanges = async () => {
    if (!store_id || !hasChanges) return;

    try {
      const reorderedCategories = categories.map((category, index) => ({
        ...category,
        sortIndex: index + 1,
      }));

      await dispatch(reorderMenuCategories({ storeId: store_id, reorderedCategories })).unwrap();
      setHasChanges(false);
    } catch (error) {
      console.error('並び順の更新に失敗しました。', error);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        カテゴリ管理
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            value={newCategoryName}
            placeholder="カテゴリ名を入力してください"
            onChange={(e) => setNewCategoryName(e.target.value)}
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleAddCategory}
            disabled={!newCategoryName}
          >
            追加
          </Button>
        </Box>
      </Paper>

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveChanges}
          startIcon={<SaveIcon />}
          disabled={!hasChanges}
        >
          並び順を保存
        </Button>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={categories?.filter(category => category && category.id).map(cat => cat.id) || []}
          strategy={verticalListSortingStrategy}
        >
          <List>
            {categories?.filter(category => category && category.id).map((category) => (
              <DraggableCategory
                key={category.id}
                category={category}
                handleDeleteCategory={handleDeleteCategory}
              />
            ))}
          </List>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default CategoryManagement;
